/**
 * WashPros Legal Footer Component
 * Displays trademark, copyright, and patent information
 *
 * WashPros® - Federally & State Registered Trademark
 * © 2026 WashPros Global Services LLC - Registered Copyright
 * Patent Pending - USPTO Provisional Patent Application
 *
 * Social Media:
 *   Instagram: @washpros.io
 *   Facebook:  @washpros.io
 *   X:         @washpros_io
 *   YouTube:   @WashProsIO
 *   TikTok:    @washpros.io
 */
import React from "react";
import { Facebook, Twitter, Instagram, Youtube } from "lucide-react";

// Social Media Links — single source of truth across the app.
const SOCIAL_LINKS = {
  instagram: {
    url: "https://instagram.com/washpros.io",
    handle: "@washpros.io",
    label: "Instagram",
  },
  facebook: {
    url: "https://facebook.com/washpros.io",
    handle: "@washpros.io",
    label: "Facebook",
  },
  twitter: {
    url: "https://x.com/washpros_io",
    handle: "@washpros_io",
    label: "X",
  },
  youtube: {
    url: "https://youtube.com/@WashProsIO",
    handle: "@WashProsIO",
    label: "YouTube",
  },
  tiktok: {
    url: "https://tiktok.com/@washpros.io",
    handle: "@washpros.io",
    label: "TikTok",
  },
};

// TikTok isn't in lucide-react; inline SVG matches the lucide 24x24 stroke look.
const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M19.321 5.562a5.122 5.122 0 0 1-3.414-1.267 5.116 5.116 0 0 1-1.537-2.73L14.37 1h-3.22v13.293a2.694 2.694 0 1 1-1.886-2.57v-3.29a5.917 5.917 0 1 0 5.106 5.86V8.604a8.306 8.306 0 0 0 4.951 1.606V7a5.085 5.085 0 0 1-.001 0h.001Z" />
  </svg>
);

const SOCIAL_ICON_MAP = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  youtube: Youtube,
  tiktok: TikTokIcon,
};

const SOCIAL_HOVER_COLOR = {
  instagram: "hover:text-pink-400",
  facebook: "hover:text-blue-400",
  twitter: "hover:text-cyan-400",
  youtube: "hover:text-red-400",
  tiktok: "hover:text-cyan-300",
};

// Stable render order (same across every footer that uses this component)
const SOCIAL_ORDER = ["instagram", "facebook", "twitter", "youtube", "tiktok"];

/**
 * Social Media Icons Component
 */
export const SocialMediaLinks = ({ className = "", showLabels = false }) => (
  // `flex-wrap` + `gap-y-2` so that when `showLabels` is true the five
  // social pills (Instagram · Facebook · X · YouTube · TikTok) can wrap
  // onto a second line on narrow viewports (390px iPhone) instead of
  // overflowing the page horizontally. Mobile pass 2026-05-17 found
  // 83px of horizontal overflow on /welcome caused by this row.
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 ${className}`}>
    {SOCIAL_ORDER.map((key) => {
      const entry = SOCIAL_LINKS[key];
      const Icon = SOCIAL_ICON_MAP[key];
      const hover = SOCIAL_HOVER_COLOR[key] || "hover:text-cyan-400";
      return (
        <a
          key={key}
          href={entry.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-1.5 text-slate-400 ${hover} transition-colors`}
          title={`Follow us on ${entry.label} ${entry.handle}`}
          data-testid={`social-${key}-link`}
        >
          <Icon className="w-4 h-4" />
          {showLabels && (
            <span className="text-xs whitespace-nowrap">
              <span className="text-slate-500">{entry.label}</span>{" "}
              {entry.handle}
            </span>
          )}
        </a>
      );
    })}
  </div>
);

/**
 * Branded "Follow WashPros" block with tagline + links. Drop in anywhere
 * you want a polished follow-us CTA rather than just the bare icons.
 */
export const FollowWashProsBlock = ({ className = "", align = "left" }) => {
  const alignCls =
    align === "center"
      ? "text-center items-center"
      : align === "right"
      ? "text-right items-end"
      : "text-left items-start";
  return (
    <div
      className={`flex flex-col ${alignCls} ${className}`}
      data-testid="follow-washpros-block"
    >
      <h4 className="text-white font-semibold mb-1">Follow WashPros</h4>
      <p className="text-slate-400 text-xs mb-3 max-w-xs">
        More content, vendor spotlights, and real transformation videos
        coming soon.
      </p>
      <SocialMediaLinks
        className="flex-col items-start gap-2"
        showLabels={true}
      />
    </div>
  );
};

/**
 * Compact legal footer for use in sidebars, modals, and small spaces
 */
export const LegalFooterCompact = ({ className = "" }) => (
  <div className={`text-center ${className}`}>
    <p className="text-slate-500 text-xs">
      © 2026 WashPros Global Services LLC. All Rights Reserved.
    </p>
    <p className="text-slate-600 text-[10px] mt-0.5">
      WashPros® is a federally registered trademark. Utility Patent Pending.
    </p>
  </div>
);

/**
 * Standard legal footer for main pages
 */
export const LegalFooterStandard = ({ className = "" }) => (
  <div className={`text-center space-y-1 ${className}`}>
    <p className="text-slate-500 text-sm">
      © 2026 <span className="font-medium">WashPros Global Services LLC</span>. All Rights Reserved.
    </p>
    <p className="text-slate-600 text-xs">
      WashPros® is a registered trademark of WashPros Global Services LLC.
      <span className="mx-1">|</span>
      Utility Patent Pending
    </p>
  </div>
);

/**
 * Full legal footer with all details for main landing pages
 */
export const LegalFooterFull = ({ className = "", showAddress = false, showSocial = true }) => (
  <div className={`text-center space-y-2 ${className}`}>
    {showSocial && (
      <div className="flex justify-center mb-3">
        <SocialMediaLinks showLabels={true} />
      </div>
    )}
    <div className="flex flex-wrap items-center justify-center gap-2 text-slate-400 text-xs">
      <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">®</span>
      <span>Federally Registered Trademark</span>
      <span className="text-slate-600">•</span>
      <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">℠</span>
      <span>FL Trademarked</span>
      <span className="text-slate-600">•</span>
      <span className="px-2 py-0.5 bg-slate-800/50 rounded text-[10px] font-medium">©</span>
      <span>Registered Copyright</span>
      <span className="text-slate-600">•</span>
      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px] font-medium">Utility Patent Pending</span>
    </div>
    <p className="text-slate-500 text-sm">
      © 2026 <span className="font-semibold text-slate-400">WashPros Global Services LLC</span>. All Rights Reserved.
    </p>
    <p className="text-slate-600 text-xs">
      WashPros® and The Car Wash Concierge Company™ are trademarks of WashPros Global Services LLC.
    </p>
    {showAddress && (
      <p className="text-slate-600 text-[10px]">
        6800 Bird Rd Suite 454, Miami, FL 33155
      </p>
    )}
  </div>
);

/**
 * Admin portal footer
 */
export const LegalFooterAdmin = ({ className = "" }) => (
  <div className={`text-center ${className}`}>
    <p className="text-slate-600 text-xs">
      © 2026 WashPros®. A subsidiary of{" "}
      <span className="text-slate-500 font-medium">WashPros Global Services LLC</span>
      <span className="mx-1.5 text-slate-700">|</span>
      <span className="text-amber-500/70">Utility Patent Pending</span>
    </p>
  </div>
);

/**
 * Brand name with registered trademark symbol
 */
export const WashProsBrand = ({ className = "", size = "default" }) => {
  const sizeClasses = {
    small: "text-sm",
    default: "text-base",
    large: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl"
  };
  
  return (
    <span className={`font-bold ${sizeClasses[size]} ${className}`}>
      WashPros<sup className="text-[0.6em] ml-0.5">®</sup>
    </span>
  );
};

/**
 * Complete legal disclosure for Terms of Service and legal pages
 */
export const LegalDisclosure = () => (
  <div className="bg-slate-900/50 border border-white/10 rounded-xl p-6 space-y-4">
    <h3 className="text-lg font-semibold text-white">Intellectual Property & Legal Notices</h3>
    
    <div className="space-y-3 text-sm text-slate-400">
      <div className="flex items-start gap-3">
        <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs font-bold flex-shrink-0">®</span>
        <div>
          <p className="text-white font-medium">Registered Trademark</p>
          <p>WashPros® is a federally registered trademark with the United States Patent and Trademark Office (USPTO) and registered at the state level. Unauthorized use is strictly prohibited.</p>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-bold flex-shrink-0">©</span>
        <div>
          <p className="text-white font-medium">Registered Copyright</p>
          <p>All content, designs, software, and intellectual property on this platform are protected under registered copyright with the U.S. Copyright Office. © 2026 WashPros Global Services LLC.</p>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs font-bold flex-shrink-0">⚡</span>
        <div>
          <p className="text-white font-medium">Patent Pending</p>
          <p>WashPros Global Services LLC has filed a provisional patent application with the United States Patent and Trademark Office (USPTO) for proprietary technology and business processes used in this platform.</p>
        </div>
      </div>
      
      <div className="flex items-start gap-3">
        <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-bold flex-shrink-0">LLC</span>
        <div>
          <p className="text-white font-medium">Corporate Structure</p>
          <p>WashPros is a wholly-owned subsidiary of WashPros Global Services LLC, a Florida Limited Liability Company.</p>
        </div>
      </div>
    </div>
    
    <div className="pt-4 border-t border-white/10">
      <p className="text-slate-500 text-xs text-center">
        For licensing inquiries, contact: legal@washpros.io
      </p>
    </div>
  </div>
);

export default {
  LegalFooterCompact,
  LegalFooterStandard,
  LegalFooterFull,
  LegalFooterAdmin,
  WashProsBrand,
  LegalDisclosure,
  SocialMediaLinks,
  FollowWashProsBlock,
  SOCIAL_LINKS
};
